import {
  useRef,
  useState,
  type PointerEvent,
  type PointerEventHandler
} from 'react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'

import {
  measureRipple,
  MAX_RIPPLES,
  type ButtonRippleHandle,
  type RippleGeometry
} from './button-ripple'

const PRESS_TRANSITION = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
  mass: 0.6
} as const

const LIFT_AND_PRESS = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.97 },
  transition: PRESS_TRANSITION
} as const

/* The lift is a `scale` transform, and Base UI positions a popup from the
   trigger's bounding box, transforms included — a trigger lifting under its own
   open menu makes the menu jitter. The press does not move the resting box, so
   it stays. */
const PRESS_ONLY = {
  whileTap: LIFT_AND_PRESS.whileTap,
  transition: PRESS_TRANSITION
} as const

/** Base UI marks a trigger whose popup is open with `data-popup-open`. */
export const hasOpenPopup = (props: Record<string, unknown>): boolean =>
  props['data-popup-open'] != null

/**
 * The press feedback Button and ButtonLink share: a 1.02 hover lift and a 0.97
 * press scale riding one spring, plus the Material ripple in `./button-ripple`.
 * All of it sits behind `animated`, and is skipped for reduced-motion users and
 * for a control that is disabled or busy; `lift` alone is withdrawn while the
 * control has a popup open. Spreading `motionProps` onto the motion element
 * and handing it `handlePointerDown` is the whole contract.
 */
export const useButtonMotion = <E extends HTMLElement>({
  animated,
  disabled = false,
  lift = true,
  onPointerDown
}: {
  animated: boolean
  disabled?: boolean
  lift?: boolean
  onPointerDown?: PointerEventHandler<E>
}) => {
  const reducedMotion = useReducedMotion()
  const ripple = useRef<ButtonRippleHandle>(null)
  const [initialRipples, setInitialRipples] = useState<RippleGeometry[]>()
  const shouldAnimate = animated && !disabled && !reducedMotion

  const handlePointerDown = (event: PointerEvent<E>) => {
    if (shouldAnimate) {
      const geometry = measureRipple(event)
      if (ripple.current) {
        ripple.current.spawn(geometry)
      } else {
        setInitialRipples(previous =>
          [...(previous ?? []), geometry].slice(-MAX_RIPPLES)
        )
      }
    }
    onPointerDown?.(event)
  }

  const motionProps = shouldAnimate
    ? lift
      ? LIFT_AND_PRESS
      : PRESS_ONLY
    : undefined

  return {
    reducedMotion,
    shouldAnimate,
    ripple,
    initialRipples,
    handlePointerDown,
    motionProps
  }
}
